import React from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";

import { Checkbox, FormGroup, Input, Select, showSnackbar } from "@cofano/cck";

import FormField from "components/cck/FormField";
import RelationContactTypeSelect from "components/selects/RelationContactTypeSelect";
import { FormDialog } from "components/dialogs/Dialogs";
import CharterContactRoleTypeSelect from "view/charters/components/selects/CharterContactRoleTypeSelect";

import { Language } from "utils";

import { gql } from "__generated__/gql";
import { ContactRoleType } from "__generated__/graphql";

import { SalesCharter, Relation } from "types/index";

type CreateCharterContactDialogProps = {
	open: boolean;
	onClose: () => void;
	charter: SalesCharter;
};

type CharterContactForm = {
	type?: string;
	relation: Relation;
	contactRoleTypes?: ContactRoleType[];
	name?: string;
	insertion?: string;
	surName?: string;
	email?: string;
	telephone?: string;
	visibleForShip: boolean;
};

const CreateCharterContactDialog = ({ open, onClose, charter }: CreateCharterContactDialogProps) => {

	const defaultValues = {
		type: undefined,
		relation: charter.debtor,
		contactRoleTypes: [],
		name: undefined,
		insertion: undefined,
		surName: undefined,
		email: undefined,
		telephone: undefined,
		visibleForShip: false,
	};

	const { control, handleSubmit, formState, reset } = useForm<CharterContactForm>({
		defaultValues: defaultValues,
	});

	const [ saveContact, { loading } ] = useMutation(SAVE_CONTACT_MUTATION);

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
	};

	const save = (data: CharterContactForm) => {
		saveContact({
			variables: {
				input: {
					...data,
					relation: data.relation.id,
					contactRoleTypes: data.contactRoleTypes ?? [],
				},
			},
		}).then(() => {
			onClose();
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_SAVED" }), { variant: "success" });
		});
	};

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "CHARTER_CREATE_CONTACT" })}
			form='createCharterContactForm'
			loading={loading}>
			<React.Fragment>
				<form onSubmit={(event) => void handleSubmit(save)(event)} id="createCharterContactForm">
					<FormGroup label={Language("TYPE")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="type"
							control={control}
							render={({ field }) => <RelationContactTypeSelect {...field} />} />
					</FormGroup>

					{charter.broker && (
						<FormGroup label={Language("RELATION")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="relation"
								control={control}
								render={({ field }) => (
									<Select
										fetchOptions={[ charter.broker, charter.debtor ]}
										value={field.value}
										onChange={option => field.onChange(option)}
										clearable={false}
										disableSearch />
								)} />
						</FormGroup>
					)}

					<FormGroup label={Language("CONTACT_ROLE_TYPE")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="contactRoleTypes"
							control={control}
							render={({ field }) => <CharterContactRoleTypeSelect multiple={true} {...field} />} />
					</FormGroup>

					<FormGroup label={Language("SHIPS_NAME")}
						labelPlacement="left-center"
						overflow="hidden"
						required>
						<FormField errors={formState.errors} name={"name"}>
							<Controller
								name="name"
								control={control}
								rules={{ required: true }}
								render={({ field }) => <Input {...field} />} />
						</FormField>
					</FormGroup>

					<FormGroup label={Language("INSERTION")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="insertion"
							control={control}
							render={({ field }) => <Input {...field} />} />
					</FormGroup>

					<FormGroup label={Language("SURNAME")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="surName"
							control={control}
							render={({ field }) => <Input {...field} />} />
					</FormGroup>

					<FormGroup label={Language("EMAIL")}
						labelPlacement="left-center"
						overflow="hidden"
						required>
						<FormField errors={formState.errors} name={"name"}>
							<Controller
								name="email"
								control={control}
								rules={{ required: true }}
								render={({ field }) => <Input {...field}  type="email" />} />
						</FormField>
					</FormGroup>

					<FormGroup label={Language("TELEPHONE")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="telephone"
							control={control}
							render={({ field }) => <Input {...field} />} />
					</FormGroup>

					<FormGroup label={Language("VISIBLE_FOR_SHIP")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name={"visibleForShip"}
							control={control}
							render={({ field }) => (
								<Checkbox {...field} checked={field.value} value={undefined} />
							)} />
					</FormGroup>
				</form>
			</React.Fragment>
		</FormDialog>
	);
};

const SAVE_CONTACT_MUTATION = gql(`#graphql
	mutation saveContactForCharter($input: ContactInput!) {
		saveContact(input: $input) {
			id
			name
		}
	}
`);

export default CreateCharterContactDialog;
